/**
 * fetchWithRetry — a resilient wrapper around a single async operation.
 *
 * Feb 24, 2026. Root cause of the "कोई ब्लॉग नहीं मिला" / "Blog not found"
 * false negatives was a single-attempt axios call in BlogPage.js and
 * BlogDetailPage.js — any transient 503 during a deploy, a mobile network
 * hiccup, or a backend cold start left the UI in a terminal empty/error
 * state until the user manually refreshed.
 *
 * Behavior:
 *   - Executes `fn()` up to `attempts` times (default 3)
 *   - Exponential backoff: 500ms → 1000ms → 2000ms
 *   - Only retries on transient failures (network error, 5xx, 408, 429)
 *   - 4xx responses (except 408/429) are returned/raised immediately — a
 *     true 404 is a legitimate "not found", not something to retry
 *   - Returns whatever `fn()` returns on success
 *   - Throws the last error if all attempts fail
 */
export async function fetchWithRetry(fn, {
  attempts = 3,
  baseDelayMs = 500,
  onAttempt,
} = {}) {
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      if (onAttempt) onAttempt(i + 1, attempts);
      return await fn();
    } catch (err) {
      lastError = err;
      if (!isTransient(err) || i === attempts - 1) {
        throw err;
      }
      const delay = baseDelayMs * Math.pow(2, i);
      await sleep(delay);
    }
  }
  throw lastError;
}

function isTransient(err) {
  // Network-level failure (no response at all)
  if (!err?.response) return true;
  const status = err.response.status;
  // Server errors + timeout + rate-limit
  return status >= 500 || status === 408 || status === 429;
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * localStorage cache helpers with versioning + stale-while-revalidate
 * semantics so a transient failure can fall back to the last known good
 * response instead of showing a false "no blogs" state.
 */
const CACHE_PREFIX = "viratastro.cache.v1.";
const DEFAULT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

export function readCache(key) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    return { data, ts, ageMs: Date.now() - ts };
  } catch {
    return null;
  }
}

export function writeCache(key, data) {
  try {
    localStorage.setItem(
      CACHE_PREFIX + key,
      JSON.stringify({ data, ts: Date.now() })
    );
  } catch {
    /* quota / disabled — ignore silently */
  }
}

export function isCacheFresh(entry, maxAgeMs = DEFAULT_MAX_AGE_MS) {
  return entry && entry.ageMs < maxAgeMs;
}
